/**
 * IntelligenceCreateDraftsPage
 * Revisão dos produtos selecionados antes de gerar rascunhos de requisição.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  ArrowLeft, FilePlus, Loader2, Package, Building2, AlertTriangle
} from 'lucide-react'
import { compraInteligenteService } from '../../../../services/compraInteligenteService'
import { useToast } from '../../../../hooks/useToast'
import { Toast } from '../../../../components'
import './CompraInteligenteDashboardPage.css'
import './IntelligenceCreateDraftsPage.css'

const RISK_LABELS = {
  critical: 'Crítico',
  high: 'Alto',
  medium: 'Médio'
}

function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value || 0)
}

function IntelligenceCreateDraftsPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { toast, showToast, hideToast } = useToast()

  const productIds = useMemo(
    () => [...new Set((location.state?.productIds || []).map(Number).filter(Boolean))],
    [location.state?.productIds]
  )
  const aiAnalysis = useMemo(
    () => location.state?.aiAnalysis || {},
    [location.state?.aiAnalysis]
  )

  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [preview, setPreview] = useState(null)
  const [qtyByProduct, setQtyByProduct] = useState({})
  const [error, setError] = useState(null)

  const loadPreview = useCallback(async () => {
    if (!productIds.length) {
      setError('Nenhum produto selecionado.')
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)
    try {
      const response = await compraInteligenteService.prepareRequisitions({
        productIds,
        aiAnalysis
      })

      if (response.success) {
        setPreview(response.data)
        const initialQty = {}
        ;(response.data?.packages || []).forEach((pkg) => {
          pkg.items.forEach((item) => {
            initialQty[item.productId] = item.orderQty
          })
        })
        setQtyByProduct(initialQty)
      } else {
        setError('Não foi possível preparar os rascunhos.')
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao carregar prévia dos rascunhos.')
    } finally {
      setLoading(false)
    }
  }, [productIds, aiAnalysis])

  useEffect(() => {
    loadPreview()
  }, [loadPreview])

  const summary = useMemo(() => {
    if (!preview?.packages) return { items: 0, packages: 0, total: 0 }

    let items = 0
    let total = 0
    preview.packages.forEach((pkg) => {
      pkg.items.forEach((item) => {
        items += 1
        const qty = qtyByProduct[item.productId] ?? item.orderQty
        total += qty * (item.unitCost || 0)
      })
    })

    return {
      items,
      packages: preview.packages.length,
      total
    }
  }, [preview, qtyByProduct])

  const handleQtyChange = (productId, value) => {
    const parsed = Math.max(0, parseInt(value, 10) || 0)
    setQtyByProduct((prev) => ({ ...prev, [productId]: parsed }))
  }

  const handleGenerate = async () => {
    const validIds = productIds.filter((id) => (qtyByProduct[id] ?? 0) > 0)
    if (!validIds.length) {
      showToast('Informe quantidade maior que zero para ao menos um produto', 'warning')
      return
    }

    setGenerating(true)
    try {
      const response = await compraInteligenteService.generateRequisitions({
        productIds: validIds,
        aiAnalysis,
        qtyOverrides: qtyByProduct
      })

      if (response.success) {
        const first = response.data?.requisitions?.[0]
        const count = response.data?.requisitions?.length || 0
        showToast(response.message || `${count} rascunho(s) criado(s)`, 'success')
        if (count === 1 && first?.id) {
          navigate(`/erp/purchases/new-request/${first.id}`)
        } else {
          navigate('/erp/purchases/draft-list')
        }
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Erro ao criar rascunhos', 'error')
    } finally {
      setGenerating(false)
    }
  }

  if (!productIds.length) {
    return (
      <div className="compra-inteligente">
        <div className="intelligence-drafts-empty">
          <p>Nenhum produto selecionado.</p>
          <button
            type="button"
            className="intelligence-drafts-back-btn"
            onClick={() => navigate('/erp/purchases/intelligence')}
          >
            <ArrowLeft size={16} /> Voltar à Compra Inteligente
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="compra-inteligente intelligence-drafts-page">
      <Toast toast={toast} onClose={hideToast} />

      <header className="intelligence-drafts-header">
        <div className="intelligence-drafts-header-main">
          <button
            type="button"
            className="intelligence-drafts-back-btn"
            onClick={() => navigate('/erp/purchases/intelligence')}
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="compra-inteligente-title">
              Criar <span className="compra-inteligente-title-highlight">Rascunhos</span>
            </h1>
            <p className="compra-inteligente-subtitle">
              Revise quantidades e fornecedores antes de gerar as requisições
            </p>
          </div>
        </div>
        <button
          type="button"
          className="intelligence-drafts-confirm-btn"
          disabled={loading || generating || !!error}
          onClick={handleGenerate}
        >
          {generating
            ? <Loader2 size={16} className="compra-inteligente-spinner" />
            : <FilePlus size={16} />}
          Gerar {summary.packages} rascunho(s)
        </button>
      </header>

      {loading && (
        <div className="compra-inteligente-loading">
          <Loader2 size={28} className="compra-inteligente-spinner" />
          <span>Preparando rascunhos...</span>
        </div>
      )}

      {error && !loading && (
        <div className="intelligence-drafts-error">
          <AlertTriangle size={20} />
          <p>{error}</p>
          <button type="button" onClick={loadPreview}>Tentar novamente</button>
        </div>
      )}

      {!loading && !error && preview && (
        <>
          <div className="intelligence-drafts-summary">
            <div>
              <span className="intelligence-drafts-summary-label">Produtos</span>
              <strong>{summary.items}</strong>
            </div>
            <div>
              <span className="intelligence-drafts-summary-label">Rascunhos</span>
              <strong>{summary.packages}</strong>
            </div>
            <div>
              <span className="intelligence-drafts-summary-label">Valor estimado</span>
              <strong>{formatCurrency(summary.total)}</strong>
            </div>
          </div>

          <div className="intelligence-drafts-packages">
            {preview.packages.map((pkg) => (
              <section key={pkg.packageKey} className="intelligence-drafts-package">
                <header className="intelligence-drafts-package-header">
                  <div>
                    <h2>
                      <Building2 size={18} />
                      {pkg.supplierName || 'Cotação multi-fornecedor'}
                    </h2>
                    <p>
                      {pkg.strategy === 'multi_quote'
                        ? 'Estratégia: cotação'
                        : 'Estratégia: pedido direto'}
                      {' · '}
                      {pkg.itemCount} item(ns) · {formatCurrency(pkg.totalValue)}
                    </p>
                  </div>
                  <span className="intelligence-drafts-package-badge">
                    <Package size={14} />
                    Rascunho
                  </span>
                </header>

                <div className="intelligence-drafts-table-wrap">
                  <table className="intelligence-drafts-table">
                    <thead>
                      <tr>
                        <th>Produto</th>
                        <th>Marca</th>
                        <th>Disponível</th>
                        <th>Em trânsito</th>
                        <th>Qtd. sugerida</th>
                        <th>Qtd. pedido</th>
                        <th>Risco</th>
                        <th>Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pkg.items.map((item) => {
                        const qty = qtyByProduct[item.productId] ?? item.orderQty
                        return (
                          <tr key={item.productId}>
                            <td>
                              <div className="intelligence-drafts-product">
                                <span>{item.name}</span>
                                {item.sku && <small>{item.sku}</small>}
                              </div>
                            </td>
                            <td>{item.brand || '—'}</td>
                            <td className="num">{item.available}</td>
                            <td className="num">{item.inTransitQty || 0}</td>
                            <td className="num">{item.suggestedQty}</td>
                            <td>
                              <input
                                type="number"
                                min="0"
                                className="intelligence-drafts-qty-input"
                                value={qty}
                                onChange={(e) => handleQtyChange(item.productId, e.target.value)}
                              />
                            </td>
                            <td>
                              <span className={`intelligence-drafts-risk intelligence-drafts-risk--${item.riskLevel}`}>
                                {RISK_LABELS[item.riskLevel] || item.riskLevel}
                              </span>
                            </td>
                            <td className="num">{formatCurrency(qty * (item.unitCost || 0))}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </section>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

export default IntelligenceCreateDraftsPage
